import { z } from 'zod';

/**
 * Append-only audit log (REQ8.1). Each row captures a state-changing action
 * by a user against a domain entity, with optional before/after snapshots.
 * `customerId` is denormalised so scoped reads use the same `customerScope`
 * helper as invoice queries.
 */
export const AuditActionEnum = z.enum([
  'user.create',
  'user.delete',
  'user.update',
  'user.disable',
  'user.password_reset',
  'user.settings.update',
  'customer.create',
  'customer.update',
  'customer.delete',
  'invoice.upload',
  'invoice.reprocess',
  'invoice.update',
  'invoice.status_change',
  'invoice.sap_sync',
  'invoice.sap_release',
  'invoice.sap_send',
  'invoice.comment.create',
  'invoice.comment.delete',
  'invoice.export',
  'invoice.delete',
  'rule.create',
  'rule.update',
  'rule.delete',
  'notification.delete',
  'notification.clear',
  // Rollback is recorded as a `rule.update` event (with `details.rollback` in
  // the audit `after` snapshot) rather than its own action, because the
  // resulting state change on the live row is identical to a manual edit.
  // Keeping the action enum compact avoids fragmenting the audit-history UI.
  // Auth events (ADR-0003). Extended in #19/#25 to cover the full
  // auth surface: login attempts, refresh-rotation incl. reuse detection,
  // password change/reset, and MFA lifecycle.
  'auth.login',
  'auth.failed_login',
  'auth.logout',
  'auth.refresh',
  'auth.refresh.reuse_detected',
  'auth.password_reset.requested',
  'auth.password_reset.confirmed',
  'auth.password_change',
  'auth.mfa.setup_initiated',
  'auth.mfa.activated',
  'auth.mfa.disabled',
  'auth.mfa.recovery_used',
]);
export type AuditAction = z.infer<typeof AuditActionEnum>;

export const AuditEntityEnum = z.enum([
  'user',
  'customer',
  'invoice',
  'rule',
  'notification',
  'session',
]);
export type AuditEntity = z.infer<typeof AuditEntityEnum>;

export const AuditEventSchema = z.object({
  id: z.string(),
  /** May be null for unauthenticated events (e.g. failed login). */
  userId: z.string().nullable(),
  customerId: z.string().nullable(),
  action: AuditActionEnum,
  entity: AuditEntityEnum,
  entityId: z.string().nullable(),
  before: z.record(z.unknown()).nullable(),
  after: z.record(z.unknown()).nullable(),
  ip: z.string().optional(),
  userAgent: z.string().optional(),
  createdAt: z.string().datetime(),
});
export type AuditEvent = z.infer<typeof AuditEventSchema>;
